import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    collection,
    query,
    orderBy,
    limit,
    getDocs,
    startAfter,
} from "firebase/firestore";
import {
    signInWithEmailAndPassword,
    onAuthStateChanged,
    signOut,
} from "firebase/auth";
import { db, auth } from "../services/firebase";
import "./AdminDashboard.css";

const PAGE_SIZE = 20;

const AdminDashboard = () => {
    const [visitors, setVisitors] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const lastDocRef = useRef(null);
    const isFetchingRef = useRef(false);
    const [user, setUser] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [authLoading, setAuthLoading] = useState(true);
    const [stats, setStats] = useState({ countries: {}, total: 0 });
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [view, setView] = useState("visitors"); // 'visitors' or 'messages'
    const [messages, setMessages] = useState([]);

    const toggleRow = (id) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedRows(newExpanded);
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setUser(user);
            setAuthLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const calculateStats = useCallback((data) => {
        const countryCounts = {};
        data.forEach((v) => {
            const country = v.country?.name || "Unknown";
            countryCounts[country] = (countryCounts[country] || 0) + 1;
        });
        setStats({
            countries: countryCounts,
            total: data.length,
        });
    }, []);

    const fetchVisitors = useCallback(
        async (isLoadMore = false) => {
            if (isFetchingRef.current) return;
            isFetchingRef.current = true;
            setLoading(true);
            setError(null);

            try {
                const visitorsCollection = collection(db, "visitors");
                let q = query(
                    visitorsCollection,
                    orderBy("timestamp", "desc"),
                    limit(PAGE_SIZE)
                );

                if (isLoadMore && lastDocRef.current) {
                    q = query(
                        visitorsCollection,
                        orderBy("timestamp", "desc"),
                        startAfter(lastDocRef.current),
                        limit(PAGE_SIZE)
                    );
                }

                const snapshot = await getDocs(q);
                const newVisitors = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                setVisitors((prev) => {
                    const updated = isLoadMore
                        ? [...prev, ...newVisitors]
                        : newVisitors;
                    calculateStats(updated);
                    return updated;
                });

                lastDocRef.current = snapshot.docs[snapshot.docs.length - 1];
                setHasMore(snapshot.docs.length === PAGE_SIZE);
            } catch (err) {
                console.error("Error fetching visitors:", err);
                setError(err.message);
            } finally {
                setLoading(false);
                isFetchingRef.current = false;
            }
        },
        [calculateStats]
    );

    const fetchMessages = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const messagesCollection = collection(db, "messages");
            const q = query(messagesCollection, orderBy("timestamp", "desc"));
            const snapshot = await getDocs(q);
            const newMessages = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setMessages(newMessages);
        } catch (err) {
            console.error("Error fetching messages:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user) {
            if (view === "visitors") {
                fetchVisitors();
            } else {
                fetchMessages();
            }
        }
    }, [user, view, fetchVisitors, fetchMessages]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
            setError("Invalid credentials. Please try again.");
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            setVisitors([]);
            setLastDoc(null);
        } catch (err) {
            console.error("Logout error:", err);
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "No Date Available";
        if (timestamp.seconds) {
            return new Date(timestamp.seconds * 1000).toLocaleString();
        }
        return new Date(timestamp).toLocaleString();
    };

    if (authLoading)
        return <div className="admin-loading">Checking authentication...</div>;

    if (!user) {
        return (
            <div className="login-container">
                <div className="login-box">
                    <h1>Admin Login</h1>
                    <form onSubmit={handleLogin}>
                        <div className="input-group">
                            <label htmlFor="email">Email</label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <div className="input-group">
                            <label htmlFor="password">Password</label>
                            <input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                        {error && <p className="error-message">{error}</p>}
                        <button type="submit" className="login-btn">
                            Login
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <header className="dashboard-header">
                <div className="header-content">
                    <h1>
                        <i className="icofont-dashboard-web mr-2"></i>
                        Admin Control Center
                    </h1>
                    <div className="user-info">
                        <span className="hide-mobile">
                            <i className="icofont-user mr-1"></i>
                            {user?.email}
                        </span>
                        <button onClick={handleLogout} className="logout-btn">
                            <i className="icofont-logout mr-1"></i>
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="dashboard-grid">
                <aside className="stats-section">
                    <div className="view-toggle mb-4">
                        <button
                            className={`toggle-btn ${
                                view === "visitors" ? "active" : ""
                            }`}
                            onClick={() => setView("visitors")}
                        >
                            <i className="icofont-users-social mr-2"></i>
                            Visitors
                        </button>
                        <button
                            className={`toggle-btn ${
                                view === "messages" ? "active" : ""
                            }`}
                            onClick={() => setView("messages")}
                        >
                            <i className="icofont-envelope mr-2"></i>
                            Messages
                        </button>
                    </div>

                    <div className="stat-card">
                        <h3>
                            <i className="icofont-users-social mr-2"></i>
                            Total{" "}
                            {view === "visitors" ? "Visitors" : "Messages"}
                        </h3>
                        <p className="stat-value">
                            {view === "visitors"
                                ? visitors.length
                                : messages.length}
                        </p>
                    </div>

                    {view === "visitors" && (
                        <div className="stat-card">
                            <h3>
                                <i className="icofont-world mr-2"></i>
                                Top Locations
                            </h3>
                            <div className="country-list">
                                {Object.entries(stats.countries)
                                    .sort((a, b) => b[1] - a[1])
                                    .slice(0, 5)
                                    .map(([country, count]) => (
                                        <div
                                            key={country}
                                            className="country-item"
                                        >
                                            <span>{country}</span>
                                            <span className="count">
                                                {count}
                                            </span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}
                </aside>

                <section className="table-section">
                    <div className="table-header">
                        <h2>
                            <i
                                className={`icofont-${
                                    view === "visitors"
                                        ? "listine-dots"
                                        : "envelope"
                                } mr-2`}
                            ></i>
                            Recent{" "}
                            {view === "visitors" ? "Activity" : "Messages"}
                        </h2>
                    </div>

                    <div className="table-container">
                        <table>
                            <thead>
                                {view === "visitors" ? (
                                    <tr>
                                        <th>Visitor</th>
                                        <th className="hide-mobile">
                                            Location
                                        </th>
                                        <th className="hide-mobile">
                                            ISP / Network
                                        </th>
                                        <th>Date & Time</th>
                                        <th>Actions</th>
                                    </tr>
                                ) : (
                                    <tr>
                                        <th>Sender</th>
                                        <th>Subject</th>
                                        <th className="hide-mobile">
                                            Message Preview
                                        </th>
                                        <th>Date & Time</th>
                                        <th>Actions</th>
                                    </tr>
                                )}
                            </thead>
                            <tbody>
                                {view === "visitors"
                                    ? visitors.map((visitor, idx) => (
                                          <React.Fragment
                                              key={`${visitor.id}-${idx}`}
                                          >
                                              <tr
                                                  className={
                                                      expandedRows.has(
                                                          visitor.id
                                                      )
                                                          ? "expanded"
                                                          : ""
                                                  }
                                                  style={{ cursor: "pointer" }}
                                                  onClick={() =>
                                                      toggleRow(visitor.id)
                                                  }
                                              >
                                                  <td>
                                                      <div className="location-cell">
                                                          <strong>
                                                              #{visitor.id}
                                                          </strong>
                                                          <span>
                                                              {visitor.ip ||
                                                                  "Unknown"}
                                                          </span>
                                                      </div>
                                                  </td>
                                                  <td className="hide-mobile">
                                                      <div className="location-cell">
                                                          <strong>
                                                              {visitor.city ||
                                                                  "Unknown City"}
                                                          </strong>
                                                          <span>
                                                              {visitor.country
                                                                  ?.name || ""}
                                                          </span>
                                                      </div>
                                                  </td>
                                                  <td className="hide-mobile">
                                                      <span className="text-muted">
                                                          {visitor.isp ||
                                                              "Unknown"}
                                                      </span>
                                                  </td>
                                                  <td>
                                                      <div className="location-cell">
                                                          <strong>
                                                              {
                                                                  formatTimestamp(
                                                                      visitor.timestamp
                                                                  ).split(
                                                                      ","
                                                                  )[0]
                                                              }
                                                          </strong>
                                                          <span>
                                                              {
                                                                  formatTimestamp(
                                                                      visitor.timestamp
                                                                  ).split(
                                                                      ","
                                                                  )[1]
                                                              }
                                                          </span>
                                                      </div>
                                                  </td>
                                                  <td>
                                                      <button
                                                          className="view-data-btn"
                                                          onClick={(e) => {
                                                              e.stopPropagation();
                                                              toggleRow(
                                                                  visitor.id
                                                              );
                                                          }}
                                                      >
                                                          {expandedRows.has(
                                                              visitor.id
                                                          )
                                                              ? "Hide Details"
                                                              : "View Full"}
                                                      </button>
                                                  </td>
                                              </tr>
                                              {expandedRows.has(visitor.id) && (
                                                  <tr className="data-row">
                                                      <td colSpan="5">
                                                          <div className="full-data-preview">
                                                              <pre>
                                                                  {JSON.stringify(
                                                                      visitor.raw_ip_data ||
                                                                          visitor,
                                                                      null,
                                                                      2
                                                                  )}
                                                              </pre>
                                                          </div>
                                                      </td>
                                                  </tr>
                                              )}
                                          </React.Fragment>
                                      ))
                                    : messages.map((msg, idx) => (
                                          <React.Fragment
                                              key={`${msg.id}-${idx}`}
                                          >
                                              <tr
                                                  className={
                                                      expandedRows.has(msg.id)
                                                          ? "expanded"
                                                          : ""
                                                  }
                                                  style={{ cursor: "pointer" }}
                                                  onClick={() =>
                                                      toggleRow(msg.id)
                                                  }
                                              >
                                                  <td>
                                                      <div className="location-cell">
                                                          <strong>
                                                              {msg.name}
                                                          </strong>
                                                          <span>
                                                              {msg.email}
                                                          </span>
                                                      </div>
                                                  </td>
                                                  <td>{msg.subject}</td>
                                                  <td className="hide-mobile">
                                                      <span className="text-muted">
                                                          {msg.message?.substring(
                                                              0,
                                                              50
                                                          )}
                                                          ...
                                                      </span>
                                                  </td>
                                                  <td>
                                                      <div className="location-cell">
                                                          <strong>
                                                              {
                                                                  formatTimestamp(
                                                                      msg.timestamp
                                                                  ).split(
                                                                      ","
                                                                  )[0]
                                                              }
                                                          </strong>
                                                          <span>
                                                              {
                                                                  formatTimestamp(
                                                                      msg.timestamp
                                                                  ).split(
                                                                      ","
                                                                  )[1]
                                                              }
                                                          </span>
                                                      </div>
                                                  </td>
                                                  <td>
                                                      <button
                                                          className="view-data-btn"
                                                          onClick={(e) => {
                                                              e.stopPropagation();
                                                              toggleRow(msg.id);
                                                          }}
                                                      >
                                                          {expandedRows.has(
                                                              msg.id
                                                          )
                                                              ? "Close"
                                                              : "Read"}
                                                      </button>
                                                  </td>
                                              </tr>
                                              {expandedRows.has(msg.id) && (
                                                  <tr className="data-row">
                                                      <td colSpan="5">
                                                          <div className="message-full-view">
                                                              <div className="message-details">
                                                                  <p>
                                                                      <strong>
                                                                          From:
                                                                      </strong>{" "}
                                                                      {msg.name}{" "}
                                                                      (
                                                                      {
                                                                          msg.email
                                                                      }
                                                                      )
                                                                  </p>
                                                                  <p>
                                                                      <strong>
                                                                          Subject:
                                                                      </strong>{" "}
                                                                      {
                                                                          msg.subject
                                                                      }
                                                                  </p>
                                                                  <hr />
                                                                  <div className="message-body">
                                                                      {
                                                                          msg.message
                                                                      }
                                                                  </div>
                                                              </div>
                                                          </div>
                                                      </td>
                                                  </tr>
                                              )}
                                          </React.Fragment>
                                      ))}
                            </tbody>
                        </table>
                    </div>

                    {hasMore && (
                        <button
                            className="load-more-btn"
                            onClick={() => fetchVisitors(true)}
                            disabled={loading}
                        >
                            {loading ? "Processing..." : "Load More Visitors"}
                        </button>
                    )}
                    {error && <p className="error-message">Error: {error}</p>}
                </section>
            </main>
        </div>
    );
};

export default AdminDashboard;
